/**
 * Der Servicebericht, den der Abschluss erzeugt — `REPORT-001`, `07` §7.8.
 *
 * Der Abschnitt endet mit dem Satz: „Servicebericht als vorbefüllten Entwurf
 * erzeugen; Freigabe und externe Veröffentlichung erfolgen erst nach
 * Büroprüfung." Beides steckt hier drin, und das zweite ist der Grund für den
 * ersten: Ein Monteur, der seinen Bericht selbst freigäbe, bräuchte keine
 * Büroprüfung — und ein Bericht, den er von Hand schreiben müsste, entstünde
 * am Ende eines langen Tages oder gar nicht.
 *
 * **Vorbefüllt heißt: aus dem, was ohnehin erfasst wurde.** Tätigkeitsnotiz,
 * Zeiten, Material und Bilder liegen am Einsatz; der Bericht sammelt sie ein
 * und tippt nichts ab. Was er **nicht** tut, ist Zusammenfassen oder
 * Formulieren — das wäre eine Aussage über die Arbeit, die niemand geprüft
 * hat, und die KI, die es könnte, liegt nach ADR-017 hinter dem Piloten.
 *
 * **Kundensicht und interne Notiz bleiben getrennt** (`07` §7.16). Die
 * Trennung entsteht hier und nicht erst beim Erzeugen des PDF: Was einmal in
 * einem gemeinsamen Feld steht, ist später nicht mehr auseinanderzuhalten.
 */

import { formatResourceRef } from "@hwc/contracts";
import type { ResourceRef } from "@hwc/contracts";
import type { Bestand, Einsatz, Servicebericht } from "@hwc/demo-data";
import { berichteZuAuftrag, ref } from "@hwc/demo-data";

import type { Einsatzauflage } from "../../offline/speicher.js";

/**
 * Erzeugt den Entwurf zu einem Einsatz.
 *
 * `undefined`, wenn zu diesem Einsatz bereits ein Bericht **vorliegt** — also
 * einer im Zustand `zur_pruefung`, `freigegeben` oder `veroeffentlicht`.
 * `07` §7.16 bindet die Freigabe an eine konkrete Version, und die darf sich
 * nicht mehr bewegen, während das Büro sie prüft.
 *
 * **Ein vorhandener Entwurf blockiert dagegen nicht — er wird aktualisiert.**
 * Bis zum 05.09.2026 sperrte diese Funktion auch dort, und der Fehler fiel
 * erst beim Durchklicken auf: Zu einem Einsatz mit Entwurf im Bestand lieferte
 * sie `undefined`, die Abschlussansicht zeigte keinen Bericht, und
 * „Bericht senden" schaltete den Zustand auf `uebermittelt`, ohne dass jemals
 * ein Bericht entstand. Der Monteur war fertig, das Büro bekam nichts, und
 * nirgends stand eine Meldung.
 *
 * Ein Entwurf ist genau das: noch nicht vorgelegt. Kennung und Version bleiben
 * deshalb erhalten — aus der Aktualisierung wird kein zweiter Bericht.
 *
 * Die **Versionsnummer** zählt über alle Berichte des Auftrags hoch, nicht
 * über die des Einsatzes. So heißt der Bericht zum Folgeeinsatz Version 2 —
 * und das ist es, was der Kunde in der Hand hält: die zweite Fassung zum
 * selben Auftrag, nicht die erste zu einem neuen Einsatz.
 */
export function erzeugeEntwurf(args: {
  readonly bestand: Bestand;
  readonly einsatz: Einsatz;
  readonly auflage: Einsatzauflage;
  readonly monteur: ResourceRef;
}): Servicebericht | undefined {
  const { bestand, einsatz, auflage, monteur } = args;

  const vorhanden = bestand.berichte.filter(
    (b) => formatResourceRef(b.einsatz) === formatResourceRef(einsatz.ref),
  );

  // Vorgelegt heißt: Das Büro hat ihn oder hatte ihn. Ab da ist er nicht mehr
  // der Entwurf des Monteurs.
  if (vorhanden.some((b) => b.zustand !== "ersetzt" && b.zustand !== "entwurf")) {
    return undefined;
  }

  /*
   * Der offene Entwurf, falls es einen gibt — dann behalten Kennung und
   * Version ihre Werte, und der Bericht bleibt derselbe.
   */
  const offenerEntwurf = vorhanden.find((b) => b.zustand === "entwurf");

  const alleZumAuftrag = berichteZuAuftrag(bestand, einsatz.auftrag);
  const version =
    offenerEntwurf?.version
    ?? Math.max(0, ...alleZumAuftrag.map((b) => b.version)) + 1;

  /**
   * Die Tätigkeit — aus der Notiz, sonst aus den erledigten Schritten.
   *
   * Der Rückfall ist kein Ersatz für die Notiz, sondern die ehrlichere
   * Alternative zu einem leeren Feld: Was abgehakt wurde, ist belegt und
   * steht ohnehin im Bericht. Der Satz davor sagt, dass es kein Fließtext
   * ist — sonst liest das Büro eine Aufzählung als Zusammenfassung.
   */
  const erledigte = einsatz.schritte
    .filter((s) => s.erledigt || auflage.erledigteSchritte.includes(s.ref.opaqueId))
    .map((s) => s.text);

  const taetigkeit = auflage.notiz?.trim()
    ? auflage.notiz.trim()
    : erledigte.length > 0
      ? `Ohne Tätigkeitsnotiz. Erledigte Arbeitsschritte:\n– ${erledigte.join("\n– ")}`
      : "Ohne Tätigkeitsnotiz und ohne abgehakte Arbeitsschritte.";

  /**
   * Offene Pflichtschritte kommen in die **interne** Notiz.
   *
   * `07` §7.8 lässt eine begründete Ausnahme zu, und `07` §7.16 hält interne
   * Notizen aus dem Kunden-PDF heraus. Beides zusammen heißt: Das Büro muss
   * sehen, was offen blieb, der Kunde nicht — er hat nichts davon zu erfahren,
   * dass ein Prüfschritt am Messgerät scheiterte, bevor jemand entschieden
   * hat, wie es weitergeht.
   */
  const offen = einsatz.schritte.filter(
    (s) => s.pflicht && !s.erledigt && !auflage.erledigteSchritte.includes(s.ref.opaqueId),
  );
  /*
   * **Begründet offen und einfach offen sind zwei verschiedene Dinge.**
   *
   * `07` §7.8 lässt den Abschluss zu, wenn die Checkliste vollständig ist
   * **oder** eine begründete Ausnahme vorliegt. Wer beides in einen Satz
   * schreibt, nimmt dem Büro die Unterscheidung, auf die es beim Prüfen
   * ankommt: Ein Schritt mit Begründung ist eine Entscheidung, einer ohne ist
   * eine Lücke.
   *
   * Die Begründung kommt aus der lokalen Auflage, wenn es eine gibt, sonst aus
   * dem Bestand. Ohne den ersten Teil bliebe genau das liegen, was der Monteur
   * vor Ort eingetippt hat — und der Hinweis in der Ansicht verspricht, dass
   * es mitgeht.
   */
  const grundZu = (schrittRef: string, ausDemBestand?: string): string | undefined =>
    auflage.ausnahmen?.[schrittRef]?.trim() || ausDemBestand;

  const interneTeile: string[] = [];

  const ohneGrund = offen.filter((s) => !grundZu(s.ref.opaqueId, s.ausnahme));
  if (ohneGrund.length > 0) {
    interneTeile.push(
      `Offene Pflichtschritte beim Abschluss: ${ohneGrund.map((s) => s.text).join("; ")}.`,
    );
  }

  for (const s of einsatz.schritte) {
    const grund = grundZu(s.ref.opaqueId, s.ausnahme);
    if (grund) interneTeile.push(`Ausnahme zu „${s.text}": ${grund}`);
  }

  const jetzt = new Date().toISOString();

  return {
    // Aus der Einsatzkennung abgeleitet und nicht zufällig: Der Entwurf
    // entsteht möglicherweise offline und wird später zugestellt. Eine
    // zufällige Kennung erzeugte bei einer doppelten Zustellung zwei
    // Berichte; diese hier ist idempotent.
    ref:
      offenerEntwurf?.ref
      ?? ref("portal", "service-report", `b-aus-${einsatz.ref.opaqueId}-v${version}`),
    einsatz: einsatz.ref,
    auftrag: einsatz.auftrag,
    version,
    // **Entwurf, nicht `zur_pruefung`.** Der Abschluss erzeugt ihn; erst das
    // Übermitteln legt ihn dem Büro vor. Zwischen beidem liegt der Blick des
    // Monteurs auf das, was er gleich abschickt (`07` §7.8: „mit sichtbarer
    // Zusammenfassung").
    zustand: "entwurf",
    erstellt: jetzt,
    erstelltVon: monteur,
    taetigkeit,
    ...(interneTeile.length > 0 ? { interneNotiz: interneTeile.join(" ") } : {}),
    zeiten: auflage.zeiten.map((z) => ref("portal", "worktime", z.id)),
    material: auflage.material.map((m) => ref("portal", "material", m.id)),
    bilder: auflage.fotos.map((f) => ref("nextcloud", "file", f.id)),
    /*
     * `ersetzt` zeigt auf den vorigen Bericht — aber nur auf einen wirklich
     * ersetzten. Ein Entwurf, der aktualisiert wird, ersetzt sich nicht
     * selbst; ein Bericht, der auf die eigene Kennung zeigt, wäre ein Zyklus
     * im Link-Graphen.
     */
    ...(() => {
      const abgeloest = vorhanden.find((b) => b.zustand === "ersetzt");
      return abgeloest && !offenerEntwurf ? { ersetzt: abgeloest.ref } : {};
    })(),
  };
}

/**
 * Eine kurze Zusammenfassung dessen, was gleich abgeschickt wird.
 *
 * `07` §7.8 verlangt „‚Einsatz abschließen' mit sichtbarer Zusammenfassung".
 * Sie zählt, statt zu bewerten: vier Zeitposten, drei Materialposten, zwei
 * Fotos. Wer etwas vergessen hat, sieht es an der Null — und eine
 * Formulierung wie „vollständig erfasst" wäre eine Behauptung, die niemand
 * geprüft hat.
 */
export function zusammenfassung(
  einsatz: Einsatz,
  auflage: Einsatzauflage,
  gebuchteZeiten: number,
  gebuchtesMaterial: number,
): readonly { readonly was: string; readonly wert: string; readonly leer: boolean }[] {
  const schritteErledigt = einsatz.schritte.filter(
    (s) => s.erledigt || auflage.erledigteSchritte.includes(s.ref.opaqueId),
  ).length;
  /*
   * **Nur unbegründet offene Pflichtschritte sind eine Lücke.**
   *
   * `07` §7.8 lässt die begründete Ausnahme ausdrücklich zu. Eine
   * Zusammenfassung, die sie trotzdem anmahnt, verlangt vom Monteur etwas,
   * das die Spezifikation nicht verlangt — und was man ihm dreimal
   * hinschreibt, liest er beim vierten Mal nicht mehr.
   */
  const pflichtOffen = einsatz.schritte.filter(
    (s) =>
      s.pflicht
      && !s.erledigt
      && !auflage.erledigteSchritte.includes(s.ref.opaqueId)
      && !(auflage.ausnahmen?.[s.ref.opaqueId]?.trim() || s.ausnahme),
  ).length;

  const zeiten = gebuchteZeiten + auflage.zeiten.length;
  const material = gebuchtesMaterial + auflage.material.length;

  return [
    {
      was: "Arbeitsschritte",
      wert: `${schritteErledigt} von ${einsatz.schritte.length}`
        + (pflichtOffen > 0
          ? ` · ${pflichtOffen} Pflichtschritt${pflichtOffen === 1 ? "" : "e"} offen`
          : ""),
      leer: pflichtOffen > 0,
    },
    { was: "Zeitposten", wert: String(zeiten), leer: zeiten === 0 },
    { was: "Materialposten", wert: String(material), leer: material === 0 },
    { was: "Fotos", wert: String(auflage.fotos.length), leer: false },
    {
      was: "Tätigkeitsnotiz",
      wert: auflage.notiz?.trim() ? "vorhanden" : "fehlt",
      leer: !auflage.notiz?.trim(),
    },
  ];
}
